// =============================================
// MediaNodeView — renders a notesMedia node (image, video, or drawing)
// =============================================
import React, { useEffect, useState } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { FiX, FiAlertTriangle, FiEdit2 } from 'react-icons/fi';
import { getMediaSrc, deleteMedia, getDrawingScene, updateDrawing, invalidateMediaSrc } from './notesMedia';
import DrawingPad, { Scene } from './DrawingPad';

const MediaNodeView: React.FC<NodeViewProps> = ({ node, editor, deleteNode, updateAttributes }) => {
  const mediaId: number = node.attrs.mediaId;
  const kind: string = node.attrs.kind || 'image';
  const name: string | undefined = node.attrs.name || undefined;
  const rev: number = node.attrs.rev || 0;
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editScene, setEditScene] = useState<Scene | null>(null);

  useEffect(() => {
    let live = true;
    if (mediaId == null) { setError(true); return; }
    setSrc(null);
    getMediaSrc(mediaId).then(u => { if (live) setSrc(u); }).catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [mediaId, rev]); // rev bumps after a drawing re-edit → refetch the new PNG

  const remove = () => {
    void deleteMedia(mediaId);
    deleteNode();
  };

  const openEditor = async () => {
    const scene = await getDrawingScene(mediaId).catch(() => null);
    setEditScene(scene);
    setEditing(true);
  };

  const onEditSave = async (scene: Scene, png: Blob) => {
    await updateDrawing(mediaId, scene, png);
    invalidateMediaSrc(mediaId);
    setEditing(false);
    updateAttributes({ rev: rev + 1 }); // force the <img> to refetch the new PNG
  };

  const isDrawing = kind === 'drawing';

  return (
    <NodeViewWrapper className="note-media-node" data-kind={kind}>
      <div className="nmn-frame">
        {editor.isEditable && (
          <div className="nmn-actions" contentEditable={false}>
            {isDrawing && (
              <button className="nmn-edit" onClick={openEditor} title="Edit drawing" aria-label="Edit drawing">
                <FiEdit2 size={13} />
              </button>
            )}
            <button className="nmn-remove" onClick={remove} title="Remove" aria-label="Remove media">
              <FiX size={14} />
            </button>
          </div>
        )}
        {error ? (
          <div className="nmn-error"><FiAlertTriangle size={16} /> Media unavailable</div>
        ) : !src ? (
          <div className="nmn-loading">Loading {kind}…</div>
        ) : kind === 'video' ? (
          <video className="nmn-video" src={src} controls preload="metadata" />
        ) : (
          <img className={isDrawing ? 'nmn-drawing' : 'nmn-image'} src={src} alt={name || (isDrawing ? 'drawing' : 'note image')} />
        )}
        {name && !isDrawing && <div className="nmn-caption" title={name}>{name}</div>}
      </div>

      {editing && (
        <DrawingPad
          title="Edit drawing"
          initialScene={editScene}
          onSave={onEditSave}
          onCancel={() => setEditing(false)}
        />
      )}
    </NodeViewWrapper>
  );
};

export default MediaNodeView;
